import React, { useState, useEffect } from "react";
// import dayjs from 'dayjs';

import { Card, Button, Grid, Stack } from "@mui/material";
import TextField from '@mui/material/TextField';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { SearchIcon as SearchRoundedIcon } from "components/icons";

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import * as AppConstants from "AppConstants";
import { loadLeavePlansFromBackendForMember, deleteLeavePlan } from "app/services/member.service";
import { checkUserHasMemberRole, readSessionCurrentMember } from "app/services/app.state.service";
import { isEmpty } from "utils/validationUtil";
// import { getCurrentMonthStartDate } from "utils/dateUtil";
import { handleAxiosError } from "app/services/app.error.handler";
import { LeavePlanDataGridBase } from "app/components/leave-plans/LeavePlanDataGridBase";
import AddLeaveDialog from "app/components/reports/AddLeaveDialog";
import { ConfirmDialog } from "app/components/ConfirmDialog";
import { useDispatch, useSelector } from "react-redux";
import { leaveActions } from "store/leave-slice";

export const LeavePlansForMember = () => {
    const dispatch = useDispatch();
    const [memberId, setMemberId] = React.useState(0); // default memberId = 10 which is for "Latesh"
    const [fromDate, setFromDate] = useState(useSelector((state) => state.leave.filter.startDate));
    const [toDate, setToDate] = useState(useSelector((state) => state.leave.filter.endDate));
    const leavePlansData = useSelector((state) => state.leave.rows);

    const [editObject, setEditObject] = useState(null);
    const [addLeaveOpen, setAddLeaveOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    useEffect(() =>  {
        // if valid login; and user role is member; then get memberId from the stored loggedInUser object
        if(!checkUserHasMemberRole()) return;

        var member = readSessionCurrentMember();

        if(isEmpty(member)) return;

        setMemberId(member.id);

    }, [memberId, fromDate, toDate]);

    const searchClickHandler = async () => {
        getLeavePlansFromBackend(memberId, fromDate, toDate); 
    }

    const getLeavePlansFromBackend = async (memberId, fromDate, toDate) => {
        dispatch(leaveActions.replaceFilter({startDate: fromDate, endDate : toDate}));
        await loadLeavePlansFromBackendForMember(memberId, fromDate, toDate)
        .then(response => { 
            dispatch(leaveActions.replaceData(response.data));
        })
        .catch(error=>{
            dispatch(leaveActions.replaceData([])); // Set the empty data in leave plans table
            handleAxiosError(error);
        });
    };

    const refresh = () => getLeavePlansFromBackend(memberId, fromDate, toDate);

    // Grid row carries the numeric id + `reason`; AddLeaveDialog needs a string id
    // (it splits on "-") and reads the reason from `description`.
    const editLeaveHandler = (row) => {
        setEditObject({ ...row, id: String(row.id), description: row.reason });
        setAddLeaveOpen(true);
    };

    const deleteLeaveHandler = (row) => {
        setDeleteTarget(row);
        setConfirmOpen(true);
    };

    const confirmDeleteHandler = async () => {
        if (!deleteTarget?.id) return;
        try {
            await deleteLeavePlan(deleteTarget.id);
            await refresh();
        } catch (error) {
            handleAxiosError(error);
        } finally {
            setDeleteTarget(null);
        }
    };

    if(!checkUserHasMemberRole()) {
        return <div>You do not have access to this functionality.</div>;
    }
    return (
        <Grid item xs={12} sm={12} md={12}>
            <Card>
                <MDBox
                    mx={2}
                    mt={-3}
                    py={1}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                >
                    <MDTypography variant="h6" color="white">
                        Leave Plans
                    </MDTypography>
                </MDBox>
                <MDBox mt={3} p={2} >
                    <Stack
                        direction="row" 
                        spacing={2}
                    >
                        <MDBox>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="From"
                                    value={fromDate}
                                    onChange={(newFromDate) => {
                                        setFromDate(newFromDate);
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                    inputFormat={AppConstants.DATE_FORMAT_DISPLAY}
                                />
                            </LocalizationProvider>
                        </MDBox>
                        <MDBox>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="To"
                                    value={toDate}
                                    onChange={(newToDate) => {
                                        setToDate(newToDate);
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                    inputFormat={AppConstants.DATE_FORMAT_DISPLAY}
                                />
                            </LocalizationProvider>
                        </MDBox>
                        <MDBox>
                            <Button 
                                onClick={searchClickHandler}
                                sx={{width:140, m: 0}}
                                variant="contained" 
                                color="success"
                                endIcon={<SearchRoundedIcon />}
                            >
                                Search
                            </Button>
                        </MDBox>
                    </Stack>
                </MDBox>

                <LeavePlanDataGridBase
                    leavePlansData={leavePlansData}
                    actions={{ onEdit: editLeaveHandler, onDelete: deleteLeaveHandler }}
                />
            </Card>

            <AddLeaveDialog
                role={AppConstants.ROLE_EMPLOYEE_MEMBER}
                addLeaveOpen={addLeaveOpen}
                setAddLeaveOpen={setAddLeaveOpen}
                refreshActivitiesDataHandler={refresh}
                editObject={editObject}
            />

            <ConfirmDialog
                title="Delete Leave"
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={confirmDeleteHandler}
                yesButtonLabel="Delete"
                noButtonLabel="Cancel"
            >
                {deleteTarget &&
                    `Are you sure you want to delete this leave (Start Date: '${deleteTarget.startDate}', Reason: '${(deleteTarget.reason ?? "").substring(0, 20)}')?`}
            </ConfirmDialog>
        </Grid>
        );
}